import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { UserType } from '../stores/authStore';
import { Box, Typography, Button, Paper } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

type UserRole = 'FREE' | 'PRO' | 'PLUS';

const userTypeLabels: Record<UserType, string> = {
    EO: 'Estate Owner', EL: 'Estate Lawyer', EX: 'Estate Executor', EW: 'Estate Witness'
};

interface RoleProtectedRouteProps {
    children: React.ReactNode;
    allowedRoles?: UserRole[];
    allowedUserTypes?: UserType[];
    /** If true, shows access denied page instead of redirecting */
    showAccessDenied?: boolean;
}

/**
 * Checks authentication plus any configured plan and estate-role restrictions.
 * A subscription never overrides the required estate role. Document ownership,
 * association and assignment permissions remain enforced by the API.
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
    children,
    allowedRoles,
    allowedUserTypes,
    showAccessDenied = true
}) => {
    const { user } = useAuthStore();
    const location = useLocation();

    // Not logged in - redirect to login
    if (!user) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    const hasPlanAccess = !allowedRoles || allowedRoles.includes(user.subscription || user.role);
    const hasUserTypeAccess = !allowedUserTypes || allowedUserTypes.includes(user.userType);
    const hasAccess = hasPlanAccess && hasUserTypeAccess;

    if (!hasAccess) {
        if (showAccessDenied) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60vh',
                        p: 3,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            maxWidth: 450,
                            borderRadius: 2,
                        }}
                    >
                        <LockOutlinedIcon
                            sx={{
                                fontSize: 64,
                                color: 'error.main',
                                mb: 2
                            }}
                        />
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {!hasUserTypeAccess ? <>
                                This page requires an <strong>{allowedUserTypes!.map(type => userTypeLabels[type]).join(' or ')}</strong> role.
                            </> : <>
                                You don't have permission to access this page. This feature requires a{' '}
                                <strong>{allowedRoles!.join(' or ')}</strong> plan.
                            </>}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            href="/"
                            sx={{ mr: 1 }}
                        >
                            Go to Dashboard
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            href="/wills"
                        >
                            View My Wills
                        </Button>
                    </Paper>
                </Box>
            );
        }

        // Redirect to dashboard without showing access denied page
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

export default RoleProtectedRoute;
